/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import constants, { publicConstants } from '../constants';
import { getContainerItemData, getTargetFromContainerItemID } from '../container/container-utils';
import { getParentContextName } from '../utils/context-utils';
import Utils from '../utils/utils';
import { didOperatorHasAssignment, isAssignmentInCreateStage } from '../utils/case-utils';
import { getStore } from '../store/state/state-manager';
import type { Action } from '../store/state/state-manager';
import type { DxAPIResponseObject } from '../case/types';
import EnvironmentInfo from '../environment-info';
import { isViewExists } from '../rules/rules-store';
import { addActionToLoadCaseSummaryView } from '../case/common-helper';
import SemanticUrlUtils from '../router/semanticurl-utils';
import CaseUtils from '../case/utils';

export const DXAPI_RESPONSE_DATA_READ_PATHS = {
  CASE_INFO: `${constants.DATA}.${constants.CASE_INFO.CASE_INFO}`,
  CASE_CLASS_NAME: `${constants.DATA}.${constants.CASE_INFO.CASE_TYPE_ID}`,
  CASE_INFO_ID: `${constants.DATA}.${constants.CASE_INFO.CASE_INFO_ID}`,
  BUSINESS_ID: `${constants.DATA}.${constants.CASE_INFO.BUSINESS_ID}`
};

export const getObjectClassName = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(
    dxApiResponse,
    constants.getProperty(DXAPI_RESPONSE_DATA_READ_PATHS.CASE_CLASS_NAME),
    constants.EMPTY_STRING
  );
};

export const getObjectInfoID = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(
    dxApiResponse,
    constants.getProperty(DXAPI_RESPONSE_DATA_READ_PATHS.CASE_INFO_ID),
    constants.EMPTY_STRING
  );
};

export const updateKeyAndFlowName = (sourceObject: object, dxApiResponse: DxAPIResponseObject) => {
  const objectInfoID = getObjectInfoID(dxApiResponse);
  Utils.setIn(sourceObject, 'key', objectInfoID);
};

export const getObjectInfo = (dxApiResponse: DxAPIResponseObject) =>
  Utils.getIn(dxApiResponse, constants.getProperty(DXAPI_RESPONSE_DATA_READ_PATHS.CASE_INFO), {});

export const isNextStepInCreateStage = (dxApiResponse: DxAPIResponseObject) => {
  const objectInfo = getObjectInfo(dxApiResponse);
  return (
    isAssignmentInCreateStage(objectInfo) &&
    didOperatorHasAssignment(EnvironmentInfo.getOperatorIdentifier(), objectInfo)
  );
};

/**
 * this function helps to check whether parent and child container data context referring to same work item or not
 * @private
 * @param childContainerItemID - child container item id
 * @returns - true --> if both container data context refers to same work item
 */
export const isParentAndChildContextHoldingSameItem = (childContainerItemID: string | null): boolean => {
  const state = getStore();

  // return if childContainerItemID is not present
  if (!childContainerItemID) {
    return false;
  }

  const parentContainerItemID = getParentContextName(childContainerItemID);
  const parentTarget = getTargetFromContainerItemID(parentContainerItemID);
  const childTarget = getTargetFromContainerItemID(childContainerItemID);

  // return false if parent target or child target are nullish values
  if (!parentTarget || !childTarget) {
    return false;
  }

  const parentItemData = getContainerItemData(parentTarget, parentContainerItemID);
  const childItemData = getContainerItemData(childTarget, childContainerItemID);

  // return false if parent item or child item data are not present
  if (!parentItemData || !childItemData) {
    return false;
  }

  const { key: parentKey } = parentItemData;
  let { key: childKey } = childItemData;
  if (!childKey) {
    childKey = state?.getState().data?.[childContainerItemID]?.objectInfo?.ID;
  }
  return parentKey && childKey && parentKey === childKey;
};

/**
 * this function helps to set last submit time property in context_data
 * @param data - data object to which refresh properties will get added
 * @param actionInPayload -  ncoming action payload from public action api
 * @private
 */
export const setLastRefreshTimeProperty = (data: any, actionInPayload: Action['payload']) => {
  const { attachmentsExist } = actionInPayload;
  if (attachmentsExist) {
    Utils.setIn(data, publicConstants.SUMMARY_OF_ATTACHMENTS_LAST_REFRESH_TIME, new Date().toLocaleString());
  }

  Utils.setIn(data, publicConstants.SUMMARY_OF_ASSIGNMENT_LAST_SUBMIT_TIME, new Date().toLocaleString());
};

export const deferCaseSummaryViewUpdate = (
  chainedData: { response: DxAPIResponseObject; actionInPayload: Action['payload'] },
  actionsOut: Action[]
) => {
  const caseClassName = getObjectClassName(chainedData?.response);
  const objectInfo = getObjectInfo(chainedData?.response);
  const { caseTypeID } = objectInfo;
  const featureMap: { deferUpdateCaseSummaryView: boolean } = EnvironmentInfo.getFeatureMap() as {
    deferUpdateCaseSummaryView: boolean;
  };
  const deferUpdateCaseSummaryView =
    featureMap?.deferUpdateCaseSummaryView ?? !Object.hasOwn(featureMap, constants.DEFER_UPDATE_CASESUMMARY_VIEW);
  /* istanbul ignore next */
  if (deferUpdateCaseSummaryView && caseTypeID && isViewExists(constants.CASESUMMARY_VIEW_NAME, caseTypeID)) {
    addActionToLoadCaseSummaryView(actionsOut, chainedData?.actionInPayload, chainedData?.response, { caseClassName });
  }
};

export const getToasterConfig = (response: DxAPIResponseObject) => {
  const caseInfoID = getObjectInfoID(response);
  const businessID = Utils.getIn(
    response,
    constants.getProperty(DXAPI_RESPONSE_DATA_READ_PATHS.BUSINESS_ID),
    constants.EMPTY_STRING
  );
  const ID = caseInfoID.toLowerCase().includes(businessID.toLowerCase()) ? businessID : caseInfoID;
  const caseType = getObjectClassName(response);

  return {
    businessID,
    caseType,
    resolvedURL: SemanticUrlUtils.getSemanticURLForToaster(caseType, ID, CaseUtils.isObjectCaseType(caseType))
  };
};
